import { useIntl } from 'react-intl';
import {
    BarChart as BarChartComp,
    Bar,
    CartesianGrid,
    ResponsiveContainer,
    XAxis,
    YAxis,
    Rectangle,
    Tooltip,
} from 'recharts';

import StatisticTooltip from 'src/features/statistic/components/StatisticTooltip';
import { DataKey, TransformedBarItem } from 'src/features/statistic/types';
import { statisticMessages } from 'src/features/statistic/utils/messages';
import GeneralMessageBlock from 'src/registry/components/blocks/GeneralMessageBlock';
import { Skeleton } from 'src/registry/ui/Skeleton';
import { ICON_MAP } from 'src/utils/constants';
import { RECHARTS_COLOR_FILLS } from 'src/features/statistic/utils/constants';
import MapLegend from 'src/features/statistic/components/MapLegend';

type Props<T> = {
    dataKeys: DataKey[];
    data: TransformedBarItem[];
    isLoading: boolean;
    colorMapping?: Record<string, string>;
    dynamicStats?: T[];
    getLabel?: (entry: T) => string;
};

type CustomShapeProps = {
    dataKeys: DataKey[];
    payload?: TransformedBarItem;
    dataKey: string;
};
const CustomShape = (props: CustomShapeProps) => {
    const { payload, dataKey, dataKeys } = props;
    const lastKey = dataKeys.findLast(
        ({ value }) => payload && Object.keys(payload).includes(value) && !!payload[value],
    )?.value;

    return <Rectangle {...props} radius={lastKey === dataKey ? [6, 6, 0, 0] : undefined} />;
};

export default function BarChart<T>({ dataKeys, data, isLoading, colorMapping, dynamicStats, getLabel }: Props<T>) {
    const { formatMessage } = useIntl();

    if (isLoading) {
        return <Skeleton className="h-90" />;
    }

    if (!data.length) {
        return (
            <GeneralMessageBlock
                icon={ICON_MAP.SEARCH}
                title={formatMessage(statisticMessages.noPaymentsFound)}
                subtitle={formatMessage(statisticMessages.chooseDifferentPeriod)}
            />
        );
    }

    return (
        <div className="flex flex-col justify-center gap-4">
            <ResponsiveContainer className="min-h-90" width="100%" height="100%">
                <BarChartComp data={data}>
                    <CartesianGrid vertical={false} strokeOpacity={0.5} />
                    <XAxis dataKey="title" />
                    <YAxis tickCount={10} />
                    <Tooltip content={(props) => <StatisticTooltip {...props} />} />
                    {dataKeys.map(({ value }, index) => {
                        return (
                            <Bar
                                key={value}
                                dataKey={value}
                                stackId="statistic"
                                fill={
                                    !dynamicStats
                                        ? colorMapping && value && colorMapping[value]
                                        : RECHARTS_COLOR_FILLS[index % RECHARTS_COLOR_FILLS.length]
                                }
                                shape={(props) => (
                                    <CustomShape
                                        dataKeys={dataKeys}
                                        dataKey={value}
                                        fill={
                                            !dynamicStats
                                                ? colorMapping && value && colorMapping[value]
                                                : RECHARTS_COLOR_FILLS[index % RECHARTS_COLOR_FILLS.length]
                                        }
                                        {...props}
                                    />
                                )}
                            />
                        );
                    })}
                </BarChartComp>
            </ResponsiveContainer>
            {dynamicStats && getLabel ? (
                <MapLegend dynamicStats={dynamicStats} getLabel={getLabel} />
            ) : (
                <MapLegend colorMapping={colorMapping} />
            )}
        </div>
    );
}
